if (!customElements.get('td-sale-terms')) {
  const SALE_TERMS_HASH = '#sale-terms';

  /**
   * Site-wide sale terms modal. Opens from any same-origin link whose hash is
   * `#sale-terms` (the path is ignored, so `/#sale-terms` works from URL settings)
   * and from `[data-td-sale-terms-open]` buttons. A page loaded with the hash opens it.
   */
  class TDSaleTerms extends HTMLElement {
    #abortController = null;
    #dialog = null;
    #previousFocus = null;
    #previousBodyOverflow = '';
    #scrollLocked = false;
    #focusFrame = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      this.#dialog = this.querySelector('[data-td-sale-terms-dialog]');
      if (!this.#dialog) return;

      const { signal } = this.#abortController;

      document.addEventListener('click', this.#handleDocumentClick, { signal });
      window.addEventListener('hashchange', this.#handleHashChange, { signal });
      this.querySelectorAll('[data-td-sale-terms-close]').forEach((button) => {
        button.addEventListener('click', this.close, { signal });
      });
      this.#dialog.addEventListener('cancel', this.#handleCancel, { signal });
      this.#dialog.addEventListener('click', this.#handleDialogClick, { signal });

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', this.#handleEditorSelect, { signal });
        document.addEventListener('shopify:block:select', this.#handleEditorSelect, { signal });
        document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect, { signal });
      }

      if (window.location.hash === SALE_TERMS_HASH) this.open();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.close();
    }

    /**
     * Opens the modal and moves focus to its close button.
     */
    open = () => {
      if (!this.#dialog || this.#dialog.open) return;

      this.#previousFocus = document.activeElement;
      this.#previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.#scrollLocked = true;
      this.#dialog.showModal();
      this.#focusFrame = requestAnimationFrame(() => {
        this.#focusFrame = null;
        this.querySelector('[data-td-sale-terms-close]')?.focus({ preventScroll: true });
      });
    };

    /**
     * Closes the modal, clears the hash so the same link can reopen it, and
     * returns focus to the element that opened it.
     */
    close = () => {
      if (this.#focusFrame) cancelAnimationFrame(this.#focusFrame);
      this.#focusFrame = null;
      if (this.#dialog?.open) this.#dialog.close();
      if (this.#scrollLocked) {
        document.body.style.overflow = this.#previousBodyOverflow;
        this.#scrollLocked = false;
      }

      if (window.location.hash === SALE_TERMS_HASH) {
        const url = new URL(window.location.href);
        url.hash = '';
        history.replaceState(history.state, '', url);
      }

      if (this.#previousFocus instanceof HTMLElement && this.#previousFocus.isConnected) {
        this.#previousFocus.focus({ preventScroll: true });
      }
      this.#previousFocus = null;
    };

    /**
     * @param {MouseEvent} event
     */
    #handleDocumentClick = (event) => {
      if (!(event.target instanceof Element) || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const trigger = event.target.closest('[data-td-sale-terms-open], a[href]');
      if (!trigger) return;

      if (trigger instanceof HTMLAnchorElement) {
        const url = new URL(trigger.href, window.location.href);
        if (url.origin !== window.location.origin || url.hash !== SALE_TERMS_HASH) return;
      } else if (!trigger.matches('[data-td-sale-terms-open]')) {
        // SVG <a href> also matches a[href]; only HTML links are checked for the hash.
        return;
      }

      event.preventDefault();
      this.open();
    };

    #handleHashChange = () => {
      if (window.location.hash === SALE_TERMS_HASH) this.open();
    };

    /**
     * @param {Event} event
     */
    #handleCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    /**
     * @param {MouseEvent} event
     */
    #handleDialogClick = (event) => {
      if (event.target === this.#dialog) this.close();
    };

    /**
     * @param {CustomEvent} event
     */
    #handleEditorSelect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.open();
    };

    /**
     * @param {CustomEvent} event
     */
    #handleEditorDeselect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.close();
    };
  }

  customElements.define('td-sale-terms', TDSaleTerms);
}
